/**
 * Reiter „Rechtsänderungen“ der Betreiberpflichten.
 *
 * Teil 1 — Änderungsliste: Änderungsgesetze und -verordnungen zu den Regelwerken des Katalogs.
 * Standard ist „Alle Rechtsänderungen“ — aktivierte Pflichten sind portfolio-/standortspezifisch
 * und daher nur ein Filter (Schalter „Nur meine aktivierten Pflichten“). Dazu Filter nach
 * Regelwerken, Ländern/Bund und regulierten Objekten, oder der Zuschnitt eines Feed-Abos;
 * Zeitraum 90 Tage / 1 Jahr / seit Datum. `?change=<guid>` klappt den Eintrag auf und scrollt hin
 * (so verlinken Feed-Einträge und `portalUrl` des Servers zurück ins Portal).
 *
 * Teil 2 — Feed-Abos: RSS-Adressen für Reader, Teams/Outlook oder CAFM. Die Adresse trägt einen
 * Zugangsschlüssel und kommt nur einmal, als Antwort auf das Anlegen; danach ist sie nicht mehr
 * abrufbar. Anlegen und Widerrufen nur mit demselben Recht wie das Verwalten von Aktivierungen
 * (`canWrite` aus dem Panel).
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Rss,
  X,
} from "lucide-react";

import type { ColumnDef } from "@tanstack/react-table";

import { useModalContext } from "../../components/ModalProvider";
import { authenticationAtom } from "../../State";
import {
  BUND,
  LAENDER,
  useLegalChangesAPI,
  type ChangeFeed,
  type ChangeFeedDocument,
  type ChangeRegulation,
  type ChangeFeedScope,
  type ChangeFilters,
  type ChangeItem,
  type CreateChangeFeedResponse,
} from "../../LegalChangesAPI";
import { formatDate, formatDateTime, regulatedObjectName } from "../../administration/operator-responsibility/legalDisplay";
import MultiSelect from "./MultiSelect";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Adressparameter, der einen Eintrag der Liste öffnet. */
export const CHANGE_PARAM = "change";

/** Zuschnitt der Liste ohne Abo: alle Rechtsänderungen, eingeengt durch die Filter. */
const ALL = "all";
const FEED_PREFIX = "feed:";
const CHANGES_LIMIT = 200;

type Period = "90" | "365" | "since";

/** YYYY-MM-DD in lokaler Zeit — `toISOString` verschöbe den Tag um Mitternacht. */
export const isoDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
};

const daysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);

  return isoDate(date);
};

/** „2026-10-01“ → „1.10.2026“ ohne UTC-Umweg. */
const formatIsoDay = (value: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");

  return match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString()
    : formatDate(value);
};

const KIND_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  substantive: "default",
  editorial: "secondary",
  repealed: "destructive",
  new: "outline",
};

interface Properties {
  canWrite: boolean;
  regulatedObjects: any[];
}

interface FilterValue {
  regulationIds: string[];
  laender: string[];
  regulatedObjectCodes: string[];
  /** Höchstens eine Richtlinie (Auswahl über MultiSelect, es zählt die zuletzt gewählte). */
  documentIds: string[];
}

const EMPTY_FILTERS: FilterValue = { regulationIds: [], laender: [], regulatedObjectCodes: [], documentIds: [] };

const hasFilters = (value: FilterValue) =>
  value.regulationIds.length > 0 ||
  value.laender.length > 0 ||
  value.regulatedObjectCodes.length > 0 ||
  value.documentIds.length > 0;

/** Nur gesetzte Felder — der Server liest ein fehlendes Feld als „keine Einschränkung“. */
const filterFields = (value: FilterValue): ChangeFilters => ({
  ...(value.regulationIds.length ? { regulationIds: value.regulationIds } : {}),
  ...(value.laender.length ? { laender: value.laender } : {}),
  ...(value.regulatedObjectCodes.length ? { regulatedObjectCodes: value.regulatedObjectCodes } : {}),
  ...(value.documentIds.length ? { documentId: value.documentIds[value.documentIds.length - 1] } : {}),
});

/** Richtlinien, die der Mandant sieht und die mit dem Rechtsbestand verknüpft sind (z. B. GEFMA 190). */
const useDocumentOptions = (enabled: boolean): ChangeFeedDocument[] => {
  const [authentication] = useAtom(authenticationAtom) as any;
  const changesAPI = useLegalChangesAPI(authentication?.customer);
  const changesAPIRef = useRef(changesAPI);
  changesAPIRef.current = changesAPI;
  const [documents, setDocuments] = useState<ChangeFeedDocument[] | null>(null);

  useEffect(() => {
    if (!enabled || documents !== null) return;
    let cancelled = false;
    changesAPIRef.current
      .getChangeFeedDocuments()
      .then((result) => {
        if (!cancelled) setDocuments(result);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, documents]);

  return documents || [];
};

/**
 * Regelwerke mit erfassten Änderungen, mit Kurzname und Titel (…/change-feeds/regulations).
 * `null` = lädt noch; leer = nichts geladen, dann bleibt die Freitexteingabe.
 */
const useRegulationOptions = (enabled: boolean): ChangeRegulation[] | null => {
  const [authentication] = useAtom(authenticationAtom) as any;
  const changesAPI = useLegalChangesAPI(authentication?.customer);
  const changesAPIRef = useRef(changesAPI);
  changesAPIRef.current = changesAPI;
  const [options, setOptions] = useState<ChangeRegulation[] | null>(null);

  useEffect(() => {
    if (!enabled || options !== null) return;
    let cancelled = false;
    changesAPIRef.current
      .getChangeRegulations()
      .then((result) => {
        if (!cancelled) setOptions(result);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, options]);

  return options;
};

/** Filterfelder Regelwerke / Länder / regulierte Objekte — in der Liste und im Feed-Dialog dieselben. */
function ScopeFilterFields(properties: {
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  regulationOptions: ChangeRegulation[] | null;
  regulatedObjects: any[];
  documentOptions: ChangeFeedDocument[];
  /** Regelwerk als Kennung eintippen (im Dialog immer, in der Liste nur ohne Katalog). */
  allowFreeText: boolean;
  layout: "row" | "stack";
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [draft, setDraft] = useState("");
  const { value, onChange } = properties;
  const width = properties.layout === "row" ? "w-[220px]" : "w-full";

  const landOptions = useMemo(
    () =>
      [BUND, ...LAENDER].map((code) => ({ key: code, text: t(`operatorObligationsPanel.changes.land.${code}`) })),
    [t],
  );
  const objectOptions = (properties.regulatedObjects || [])
    .filter((object: any) => object?.code)
    .map((object: any) => ({ key: String(object.code), text: regulatedObjectName(t, object.code, object.name) }));
  const knownRegulations = new Map((properties.regulationOptions || []).map((r) => [r.eliId, r]));
  const regulationLabel = (id: string) => {
    const r = knownRegulations.get(id);
    return r ? `${r.shortName}${r.title ? ` — ${r.title}` : ""}` : id;
  };
  const regulationSelectOptions = Array.from(
    new Set([...(properties.regulationOptions || []).map((r) => r.eliId), ...value.regulationIds]),
  ).map((id) => ({ key: id, text: regulationLabel(id) }));

  const addDraft = () => {
    const entry = draft.trim();
    if (!entry) return;
    if (!value.regulationIds.includes(entry)) onChange({ ...value, regulationIds: [...value.regulationIds, entry] });
    setDraft("");
  };

  const draftId = `${fieldId}-regulation-draft`;

  return (
    <div
      className={
        properties.layout === "row" ? "flex min-w-0 flex-wrap items-end gap-4" : "flex min-w-0 flex-col gap-4"
      }
    >
      <div className={`flex min-w-0 ${properties.layout === "row" ? "" : "w-full"} flex-col gap-2`}>
        {properties.regulationOptions === null ? (
          <div className={`flex ${width} max-w-full flex-col gap-1.5`}>
            <Label>{t("operatorObligationsPanel.changes.regulations")}</Label>
            <div className="flex h-9 items-center gap-2 text-sm text-muted-foreground">
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              {t("operatorObligationsPanel.changes.regulationsLoading")}
            </div>
          </div>
        ) : regulationSelectOptions.length > 0 ? (
          <MultiSelect
            label={t("operatorObligationsPanel.changes.regulations")}
            placeholder={t("operatorObligationsPanel.changes.filterAll")}
            emptyLabel={t("global.noInformationAvailable")}
            options={regulationSelectOptions}
            selectedKeys={value.regulationIds}
            onChange={(regulationIds) => onChange({ ...value, regulationIds })}
            widthClassName={width}
          />
        ) : (
          <Label htmlFor={draftId}>{t("operatorObligationsPanel.changes.regulations")}</Label>
        )}
        {properties.allowFreeText || properties.regulationOptions?.length === 0 ? (
          <>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Input
                id={draftId}
                aria-label={t("operatorObligationsPanel.changes.regulationDraft")}
                className="w-[220px] max-w-full"
                value={draft}
                placeholder={t("operatorObligationsPanel.changes.regulationDraftPlaceholder")}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addDraft();
                  }
                }}
              />
              <Button variant="outline" size="sm" disabled={!draft.trim()} onClick={addDraft}>
                {t("global.add")}
              </Button>
            </div>
            {value.regulationIds.length > 0 ? (
              <div className="flex min-w-0 flex-wrap gap-1.5">
                {value.regulationIds.map((id) => (
                  <Badge key={id} variant="secondary" className="gap-1">
                    <span className="break-all">{regulationLabel(id)}</span>
                    <button
                      type="button"
                      className="rounded-sm opacity-70 hover:opacity-100"
                      aria-label={t("operatorObligationsPanel.changes.removeRegulation", { id })}
                      onClick={() =>
                        onChange({ ...value, regulationIds: value.regulationIds.filter((candidate) => candidate !== id) })
                      }
                    >
                      <X aria-hidden className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      <MultiSelect
        label={t("operatorObligationsPanel.changes.laender")}
        placeholder={t("operatorObligationsPanel.changes.filterAll")}
        emptyLabel={t("global.noInformationAvailable")}
        options={landOptions}
        selectedKeys={value.laender}
        onChange={(laender) => onChange({ ...value, laender })}
        widthClassName={width}
      />
      {properties.documentOptions.length > 0 ? (
        <MultiSelect
          label={t("operatorObligationsPanel.changes.document")}
          placeholder={t("operatorObligationsPanel.changes.filterAll")}
          emptyLabel={t("global.noInformationAvailable")}
          options={properties.documentOptions.map((document) => ({ key: document.id, text: document.title }))}
          selectedKeys={value.documentIds}
          onChange={(documentIds) => onChange({ ...value, documentIds: documentIds.slice(-1) })}
          widthClassName={width}
        />
      ) : null}
      {objectOptions.length > 0 ? (
        <MultiSelect
          label={t("operatorObligationsPanel.changes.regulatedObjects")}
          placeholder={t("operatorObligationsPanel.changes.filterAll")}
          emptyLabel={t("global.noInformationAvailable")}
          options={objectOptions}
          selectedKeys={value.regulatedObjectCodes}
          onChange={(regulatedObjectCodes) => onChange({ ...value, regulatedObjectCodes })}
          widthClassName={width}
        />
      ) : null}
    </div>
  );
}

export default function LegalChangesPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string = authentication?.customer;
  const changesAPI = useLegalChangesAPI(customer);
  const changesAPIRef = useRef(changesAPI);
  changesAPIRef.current = changesAPI;
  const [searchParams] = useSearchParams();
  const focusGuid = searchParams.get(CHANGE_PARAM);
  const fieldId = useId();

  // Feed-Abos
  const [feeds, setFeeds] = useState<ChangeFeed[]>([]);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [feedsError, setFeedsError] = useState(false);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Änderungsliste
  const [scopeValue, setScopeValue] = useState<string>(ALL);
  const [filters, setFilters] = useState<FilterValue>(EMPTY_FILTERS);
  const [onlyActivated, setOnlyActivated] = useState(false);
  const [period, setPeriod] = useState<Period>("90");
  const [sinceDate, setSinceDate] = useState<string>(daysAgo(30));
  const [items, setItems] = useState<ChangeItem[]>([]);
  const [scopeLabel, setScopeLabel] = useState("");
  const [changesLoading, setChangesLoading] = useState(true);
  const [changesError, setChangesError] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const changesRequestId = useRef(0);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const handledFocus = useRef<string | null>(null);

  const since = period === "since" ? sinceDate : daysAgo(period === "365" ? 365 : 90);
  const activeFeeds = feeds.filter((feed) => !feed.revoked);
  const feedSelected = scopeValue.startsWith(FEED_PREFIX);
  const regulationOptions = useRegulationOptions(!feedSelected);
  const documentOptions = useDocumentOptions(!feedSelected);
  // Stabile Abhängigkeit für den Ladeeffekt (die Arrays wechseln bei jeder Auswahl die Identität).
  const filterKey = JSON.stringify([filters, onlyActivated]);

  const loadFeeds = async () => {
    if (!customer) {
      setFeedsLoading(false);
      return;
    }
    setFeedsLoading(true);
    try {
      setFeeds(await changesAPIRef.current.getChangeFeeds());
      setFeedsError(false);
    } catch (error) {
      setFeedsError(true);
      pushError(error);
    } finally {
      setFeedsLoading(false);
    }
  };

  useEffect(() => {
    loadFeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  // Ein widerrufenes oder verschwundenes Abo kann kein Zuschnitt mehr sein.
  useEffect(() => {
    if (scopeValue.startsWith(FEED_PREFIX) && !feedsLoading) {
      const feedId = scopeValue.slice(FEED_PREFIX.length);
      if (!activeFeeds.some((feed) => feed.id === feedId)) setScopeValue(ALL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeds, feedsLoading, scopeValue]);

  useEffect(() => {
    if (!customer) {
      setChangesLoading(false);
      return;
    }
    if (period === "since" && !/^\d{4}-\d{2}-\d{2}$/.test(sinceDate)) return;

    const requestId = ++changesRequestId.current;
    setChangesLoading(true);
    setChangesError(false);
    const query = scopeValue.startsWith(FEED_PREFIX)
      ? { feedId: scopeValue.slice(FEED_PREFIX.length), since, limit: CHANGES_LIMIT }
      : {
          ...(onlyActivated ? { mode: "activatedObligations" as const } : {}),
          ...filterFields(filters),
          since,
          limit: CHANGES_LIMIT,
        };

    changesAPIRef.current
      .getChanges(query)
      .then((result) => {
        if (changesRequestId.current !== requestId) return;
        setItems(result.items);
        setScopeLabel(result.scopeLabel);
      })
      .catch(() => {
        if (changesRequestId.current !== requestId) return;
        setItems([]);
        setChangesError(true);
      })
      .finally(() => {
        if (changesRequestId.current === requestId) setChangesLoading(false);
      });
    // filters/onlyActivated stecken in filterKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, scopeValue, since, period, sinceDate, reloadSignal, filterKey]);

  // Deep-Link: Eintrag aufklappen und hinscrollen — einmal je guid und geladener Liste.
  useEffect(() => {
    if (!focusGuid || changesLoading) return;
    if (!items.some((item) => item.guid === focusGuid)) return;
    if (handledFocus.current === focusGuid) return;
    handledFocus.current = focusGuid;
    setExpanded((current) => new Set(current).add(focusGuid));
    const element = itemRefs.current[focusGuid];
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusGuid, items, changesLoading]);

  const focusMissing =
    !!focusGuid && !changesLoading && !changesError && !items.some((item) => item.guid === focusGuid);

  const toggle = (guid: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });

  const revoke = async (feed: ChangeFeed) => {
    setRevokingId(feed.id);
    try {
      await changesAPIRef.current.revokeChangeFeed(feed.id);
      setConfirmRevokeId(null);
      await loadFeeds();
    } catch (error) {
      pushError(error);
    } finally {
      setRevokingId(null);
    }
  };

  const landName = (land: string | null) => t(`operatorObligationsPanel.changes.land.${land || BUND}`);

  const feedColumns: ColumnDef<ChangeFeed>[] = [
    {
      id: "name",
      accessorFn: (feed) => feed.name,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.changes.feedName")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="block max-w-[220px] break-words text-sm">{row.original.name}</span>
          {row.original.revoked ? (
            <Badge variant="secondary">{t("operatorObligationsPanel.changes.revoked")}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: "scope",
      accessorFn: (feed) => feed.scopeLabel,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.changes.scope")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[320px] break-words text-sm">{row.original.scopeLabel || "—"}</span>
      ),
    },
    {
      id: "createdAt",
      accessorFn: (feed) => feed.createdAt,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.changes.createdAt")}</span>,
      enableSorting: false,
      cell: ({ row }) => <span className="text-sm">{formatDate(row.original.createdAt)}</span>,
    },
    {
      id: "lastFetchedAt",
      accessorFn: (feed) => feed.lastFetchedAt,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.changes.lastFetchedAt")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.lastFetchedAt
            ? formatDateTime(row.original.lastFetchedAt)
            : t("operatorObligationsPanel.changes.neverFetched")}
        </span>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">{t("operatorObligationsPanel.changes.revoke")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const feed = row.original;

        if (!properties.canWrite || feed.revoked) return null;
        if (confirmRevokeId === feed.id) {
          return (
            <div className="flex min-w-0 flex-wrap items-center gap-2" role="group" aria-label={t("operatorObligationsPanel.changes.revokeConfirmQuestion")}>
              <span className="max-w-[260px] break-words text-sm">
                {t("operatorObligationsPanel.changes.revokeConfirmQuestion")}
              </span>
              <Button
                variant="destructive"
                size="sm"
                className="gap-2"
                disabled={revokingId === feed.id}
                onClick={() => revoke(feed)}
              >
                {revokingId === feed.id ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                {t("operatorObligationsPanel.changes.revokeConfirm")}
              </Button>
              <Button variant="outline" size="sm" disabled={revokingId === feed.id} onClick={() => setConfirmRevokeId(null)}>
                {t("global.cancel")}
              </Button>
            </div>
          );
        }

        return (
          <Button variant="outline" size="sm" onClick={() => setConfirmRevokeId(feed.id)}>
            {t("operatorObligationsPanel.changes.revoke")}
          </Button>
        );
      },
    },
  ];

  const scopeSelectId = `${fieldId}-scope`;
  const periodSelectId = `${fieldId}-period`;
  const sinceInputId = `${fieldId}-since`;
  const onlyActivatedId = `${fieldId}-only-activated`;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {/* ------------------------------------------------ Änderungsliste */}
      <section className="flex min-w-0 flex-col gap-4" aria-labelledby={`${fieldId}-list-title`}>
        <div className="flex min-w-0 flex-col gap-1">
          <h3 id={`${fieldId}-list-title`} className="text-base font-semibold">
            {t("operatorObligationsPanel.changes.listTitle")}
          </h3>
          <p className="min-w-0 break-words text-sm text-muted-foreground">
            {t("operatorObligationsPanel.changes.listIntro")}
          </p>
        </div>

        <div className="flex min-w-0 flex-wrap items-end gap-4">
          <div className="flex w-[280px] max-w-full flex-col gap-1.5">
            <Label htmlFor={scopeSelectId}>{t("operatorObligationsPanel.changes.scope")}</Label>
            <Select value={scopeValue} onValueChange={setScopeValue}>
              <SelectTrigger id={scopeSelectId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("operatorObligationsPanel.changes.scopeAll")}</SelectItem>
                {activeFeeds.map((feed) => (
                  <SelectItem key={feed.id} value={`${FEED_PREFIX}${feed.id}`}>
                    {t("operatorObligationsPanel.changes.scopeFeed", { name: feed.name })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[180px] max-w-full flex-col gap-1.5">
            <Label htmlFor={periodSelectId}>{t("operatorObligationsPanel.changes.period")}</Label>
            <Select value={period} onValueChange={(value) => setPeriod(value as Period)}>
              <SelectTrigger id={periodSelectId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="90">{t("operatorObligationsPanel.changes.period90")}</SelectItem>
                <SelectItem value="365">{t("operatorObligationsPanel.changes.period365")}</SelectItem>
                <SelectItem value="since">{t("operatorObligationsPanel.changes.periodSince")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period === "since" ? (
            <div className="flex w-[180px] max-w-full flex-col gap-1.5">
              <Label htmlFor={sinceInputId}>{t("operatorObligationsPanel.changes.sinceDate")}</Label>
              <Input
                id={sinceInputId}
                type="date"
                value={sinceDate}
                max={isoDate(new Date())}
                onChange={(event) => setSinceDate(event.target.value)}
              />
            </div>
          ) : null}
        </div>

        {feedSelected ? null : (
          <div className="flex min-w-0 flex-col gap-3 rounded-md border p-3">
            <ScopeFilterFields
              value={filters}
              onChange={setFilters}
              regulationOptions={regulationOptions}
              regulatedObjects={properties.regulatedObjects}
              documentOptions={documentOptions}
              allowFreeText={false}
              layout="row"
            />
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch id={onlyActivatedId} checked={onlyActivated} onCheckedChange={setOnlyActivated} />
                <Label htmlFor={onlyActivatedId}>{t("operatorObligationsPanel.changes.onlyActivated")}</Label>
              </div>
              {hasFilters(filters) || onlyActivated ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    setFilters(EMPTY_FILTERS);
                    setOnlyActivated(false);
                  }}
                >
                  <X aria-hidden className="h-4 w-4" />
                  {t("operatorObligationsPanel.changes.resetFilters")}
                </Button>
              ) : null}
            </div>
          </div>
        )}

        {scopeLabel && !changesLoading && !changesError ? (
          <p className="min-w-0 break-words text-xs text-muted-foreground">
            {t("operatorObligationsPanel.changes.scopeLabel", { label: scopeLabel })}
          </p>
        ) : null}

        {focusMissing ? (
          <Alert variant="info">
            <Info aria-hidden className="h-4 w-4" />
            <AlertDescription className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="min-w-0 break-words">{t("operatorObligationsPanel.changes.focusMissing")}</span>
              {period === "90" ? (
                <Button variant="outline" size="sm" onClick={() => setPeriod("365")}>
                  {t("operatorObligationsPanel.changes.period365")}
                </Button>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}

        {changesLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground" role="status">
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            {t("operatorObligationsPanel.changes.loading")}
          </div>
        ) : changesError ? (
          <Alert variant="destructive">
            <AlertTriangle aria-hidden className="h-4 w-4" />
            <AlertTitle>{t("operatorObligationsPanel.changes.loadFailedTitle")}</AlertTitle>
            <AlertDescription className="flex min-w-0 flex-col items-start gap-2">
              <span className="min-w-0 break-words">{t("operatorObligationsPanel.changes.loadFailed")}</span>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => setReloadSignal((value) => value + 1)}>
                <RefreshCw aria-hidden className="h-4 w-4" />
                {t("global.refresh")}
              </Button>
            </AlertDescription>
          </Alert>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-md border py-6 text-center">
            <div className="text-sm font-semibold text-foreground">{t("operatorObligationsPanel.changes.empty")}</div>
            <div className="max-w-prose text-sm text-muted-foreground">
              {feedSelected
                ? t("operatorObligationsPanel.changes.emptyHintFeed")
                : hasFilters(filters) || onlyActivated
                  ? t("operatorObligationsPanel.changes.emptyHintFiltered")
                  : t("operatorObligationsPanel.changes.emptyHintAll")}
            </div>
            {period !== "365" ? (
              <Button variant="outline" size="sm" onClick={() => setPeriod("365")}>
                {t("operatorObligationsPanel.changes.period365")}
              </Button>
            ) : null}
          </div>
        ) : (
          <ul className="flex min-w-0 flex-col gap-3" aria-label={t("operatorObligationsPanel.changes.listTitle")}>
            {items.map((item) => {
              const open = expanded.has(item.guid);
              const detailsId = `${fieldId}-details-${item.guid}`;

              return (
                <li key={item.guid}>
                  <div
                    ref={(element) => {
                      itemRefs.current[item.guid] = element;
                    }}
                    data-change-guid={item.guid}
                    className={`flex min-w-0 flex-col gap-2 rounded-md border p-4 ${
                      focusGuid === item.guid ? "border-primary ring-1 ring-primary" : ""
                    }`}
                  >
                    <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <div className="min-w-0 break-words font-semibold">
                          {item.regulation}
                          {item.regulationTitle ? (
                            <span className="font-normal text-muted-foreground"> · {item.regulationTitle}</span>
                          ) : null}
                        </div>
                        <div className="min-w-0 break-words text-sm">
                          {item.act}
                          {item.citation ? <span className="text-muted-foreground"> · {item.citation}</span> : null}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={item.status === "scheduled" ? "warning" : "success"}>
                          {item.status === "scheduled"
                            ? t("operatorObligationsPanel.changes.scheduledFrom", { date: formatIsoDay(item.effectiveDate) })
                            : t("operatorObligationsPanel.changes.inForceFrom", { date: formatIsoDay(item.effectiveDate) })}
                        </Badge>
                        <Badge variant="outline">{landName(item.land)}</Badge>
                      </div>
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <span className={item.impactedObligations > 0 ? "" : "text-muted-foreground"}>
                          {t("operatorObligationsPanel.changes.impacted", { count: item.impactedObligations || 0 })}
                        </span>
                        {item.impactedObligations > 0 && typeof item.confirmedObligations === "number" ? (
                          <span className="text-xs text-muted-foreground" data-testid="review-status">
                            {t("operatorObligationsPanel.changes.reviewStatus", {
                              confirmed: item.confirmedObligations,
                              unconfirmed: item.impactedObligations - item.confirmedObligations,
                            })}
                          </span>
                        ) : null}
                        {item.activatedObligations > 0 ? (
                          <Badge variant="default" data-testid="activated-obligations">
                            {t("operatorObligationsPanel.changes.impactedActivated", {
                              count: item.activatedObligations,
                            })}
                          </Badge>
                        ) : null}
                      </span>
                      {item.officialUrl ? (
                        <a
                          href={item.officialUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                        >
                          <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                          {t("operatorObligationsPanel.changes.officialText")}
                        </a>
                      ) : null}
                      {item.provisions.length > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1 px-2"
                          aria-expanded={open}
                          aria-controls={detailsId}
                          onClick={() => toggle(item.guid)}
                        >
                          {open ? <ChevronDown aria-hidden className="h-4 w-4" /> : <ChevronRight aria-hidden className="h-4 w-4" />}
                          {t("operatorObligationsPanel.changes.provisionsToggle", { count: item.provisions.length })}
                        </Button>
                      ) : null}
                    </div>
                    {open ? (
                      <ul id={detailsId} className="flex min-w-0 flex-col gap-2 border-t pt-2">
                        {item.provisions.map((provision) => (
                          <li key={provision.provisionId} className="flex min-w-0 flex-col gap-1">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                              <span className="font-medium">{provision.provisionId}</span>
                              <Badge variant={KIND_VARIANTS[provision.kind] || "secondary"}>
                                {t(`operatorObligationsPanel.changes.kind.${provision.kind}`)}
                              </Badge>
                            </div>
                            {provision.summary ? (
                              <p className="min-w-0 break-words text-sm text-muted-foreground">{provision.summary}</p>
                            ) : null}
                            <div className="flex flex-wrap items-center gap-4 text-sm">
                              {provision.url ? (
                                <a
                                  href={provision.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                                >
                                  <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                                  {t("operatorObligationsPanel.changes.officialText")}
                                </a>
                              ) : null}
                              {provision.synopsisUrl ? (
                                <a
                                  href={provision.synopsisUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                                >
                                  <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                                  {t("operatorObligationsPanel.changes.synopsis")}
                                </a>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ------------------------------------------------ Feed-Abos */}
      <section className="flex min-w-0 flex-col gap-4" aria-labelledby={`${fieldId}-feeds-title`}>
        <div className="flex min-w-0 flex-col gap-1">
          <h3 id={`${fieldId}-feeds-title`} className="text-base font-semibold">
            {t("operatorObligationsPanel.changes.feedsTitle")}
          </h3>
          <p className="min-w-0 break-words text-sm text-muted-foreground">
            {t("operatorObligationsPanel.changes.feedsIntro")}
          </p>
        </div>
        {properties.canWrite ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Button
              data-guide-id="settings.operatorObligations.createChangeFeed"
              className="gap-2 max-sm:w-full"
              onClick={() => setCreateOpen(true)}
            >
              <Plus aria-hidden className="h-4 w-4" />
              {t("operatorObligationsPanel.changes.createFeed")}
            </Button>
          </div>
        ) : (
          <Alert variant="warning">
            <AlertDescription>{t("operatorObligationsPanel.changes.writeRequiresAdmin")}</AlertDescription>
          </Alert>
        )}
        {feedsError && !feedsLoading ? (
          <Alert variant="destructive">
            <AlertTriangle aria-hidden className="h-4 w-4" />
            <AlertDescription className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="min-w-0 break-words">{t("operatorObligationsPanel.changes.feedsLoadFailed")}</span>
              <Button variant="outline" size="sm" className="gap-2" onClick={loadFeeds}>
                <RefreshCw aria-hidden className="h-4 w-4" />
                {t("global.refresh")}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="min-w-0 overflow-x-auto">
          <DataTable
            columns={feedColumns}
            data={feeds}
            loading={feedsLoading}
            pageSize={5}
            previousAriaLabel={t("global.back")}
            nextAriaLabel={t("global.next")}
            emptyMessage={
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <Rss aria-hidden className="h-5 w-5 text-muted-foreground" />
                <div className="text-sm font-semibold text-foreground">{t("operatorObligationsPanel.changes.noFeeds")}</div>
                <div className="max-w-prose text-sm text-muted-foreground">
                  {t("operatorObligationsPanel.changes.noFeedsHint")}
                </div>
              </div>
            }
          />
        </div>
      </section>

      {createOpen ? (
        <CreateFeedDialog
          regulatedObjects={properties.regulatedObjects}
          onClose={() => setCreateOpen(false)}
          onCreated={() => loadFeeds()}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------
// Dialog „Feed anlegen“
// ------------------------------------------------------------------

function CreateFeedDialog(properties: {
  regulatedObjects: any[];
  onClose: () => void;
  onCreated: (result: CreateChangeFeedResponse) => void;
}) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const changesAPI = useLegalChangesAPI(authentication?.customer);
  const fieldId = useId();

  const [name, setName] = useState("");
  const [mode, setMode] = useState<ChangeFeedScope["mode"]>("all");
  const [filters, setFilters] = useState<FilterValue>(EMPTY_FILTERS);
  const regulationOptions = useRegulationOptions(mode === "custom");
  const documentOptions = useDocumentOptions(mode === "custom");
  const [submitting, setSubmitting] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [created, setCreated] = useState<CreateChangeFeedResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const urlInputRef = useRef<HTMLInputElement | null>(null);

  const customValid = hasFilters(filters);
  const valid = name.trim().length > 0 && (mode !== "custom" || customValid);

  const submit = async () => {
    const scope: ChangeFeedScope = mode === "custom" ? { mode, ...filterFields(filters) } : { mode };
    setSubmitting(true);
    setForbidden(false);
    try {
      const result = await changesAPI.createChangeFeed(name.trim(), scope);
      setCreated(result);
      properties.onCreated(result);
    } catch (error: any) {
      if (error?.status === 403) setForbidden(true);
      else pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const selectUrl = () => {
    urlInputRef.current?.focus();
    urlInputRef.current?.select();
  };

  const copyUrl = async () => {
    if (!created) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(created.feedUrl);
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ohne Zwischenablage (http, Rechte verweigert): markieren, damit ⌘C/Strg+C genügt.
      setCopyFailed(true);
      selectUrl();
    }
  };

  const nameId = `${fieldId}-name`;
  const urlId = `${fieldId}-url`;
  const modeHint: Record<ChangeFeedScope["mode"], string> = {
    all: t("operatorObligationsPanel.changes.scopeAllHint"),
    custom: t("operatorObligationsPanel.changes.scopeCustomHint"),
    activatedObligations: t("operatorObligationsPanel.changes.scopeActivatedHint"),
  };
  const modes: Array<{ value: ChangeFeedScope["mode"]; label: string }> = [
    { value: "all", label: t("operatorObligationsPanel.changes.scopeAll") },
    { value: "custom", label: t("operatorObligationsPanel.changes.scopeCustom") },
    { value: "activatedObligations", label: t("operatorObligationsPanel.changes.onlyActivated") },
  ];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) properties.onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:max-w-[640px]"
        // Solange die Adresse steht, schließt nur ein bewusster Klick — sie kommt nicht wieder.
        onInteractOutside={(event) => { if (created) event.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>
            {created ? t("operatorObligationsPanel.changes.feedCreated") : t("operatorObligationsPanel.changes.createFeed")}
          </DialogTitle>
          <DialogDescription>
            {created ? created.feed?.name : t("operatorObligationsPanel.changes.createFeedSubtitle")}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="flex min-w-0 flex-col gap-4">
            <Alert variant="warning">
              <AlertTriangle aria-hidden className="h-4 w-4" />
              <AlertDescription>{t("operatorObligationsPanel.changes.feedUrlWarning")}</AlertDescription>
            </Alert>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={urlId}>{t("operatorObligationsPanel.changes.feedUrl")}</Label>
              <Input
                id={urlId}
                ref={urlInputRef}
                value={created.feedUrl}
                readOnly
                className="font-mono text-xs"
                onFocus={(event) => event.target.select()}
              />
              {copyFailed ? (
                <p className="text-xs text-muted-foreground" role="status">
                  {t("operatorObligationsPanel.changes.copyFallback")}
                </p>
              ) : null}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={properties.onClose}>
                {t("global.close")}
              </Button>
              <Button className="gap-2" onClick={copyUrl}>
                {copied ? <Check aria-hidden className="h-4 w-4" /> : <Copy aria-hidden className="h-4 w-4" />}
                {copied ? t("global.copied") : t("operatorObligationsPanel.changes.copyUrl")}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={nameId}>
                {t("operatorObligationsPanel.changes.feedName")}
                <span aria-hidden className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id={nameId}
                value={name}
                maxLength={120}
                placeholder={t("operatorObligationsPanel.changes.feedNamePlaceholder")}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <Label id={`${fieldId}-mode`}>{t("operatorObligationsPanel.changes.scope")}</Label>
              <RadioGroup
                aria-labelledby={`${fieldId}-mode`}
                value={mode}
                onValueChange={(value) => setMode(value as ChangeFeedScope["mode"])}
              >
                {modes.map((option) => (
                  <div key={option.value} className="flex items-center gap-2">
                    <RadioGroupItem id={`${fieldId}-mode-${option.value}`} value={option.value} />
                    <Label htmlFor={`${fieldId}-mode-${option.value}`} className="font-normal">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p className="min-w-0 break-words text-xs text-muted-foreground">{modeHint[mode]}</p>
            </div>

            {mode === "custom" ? (
              <div className="flex min-w-0 flex-col gap-3 rounded-md border p-3">
                <ScopeFilterFields
                  value={filters}
                  onChange={setFilters}
                  regulationOptions={regulationOptions}
                  regulatedObjects={properties.regulatedObjects}
                  documentOptions={documentOptions}
                  allowFreeText
                  layout="stack"
                />
                {!customValid ? (
                  <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.changes.customRequired")}</p>
                ) : null}
              </div>
            ) : null}

            {forbidden ? (
              <Alert variant="destructive">
                <AlertDescription>{t("operatorObligationsPanel.changes.forbidden")}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={properties.onClose}>
                {t("global.cancel")}
              </Button>
              <Button className="gap-2" disabled={!valid || submitting} onClick={submit}>
                {submitting ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Rss aria-hidden className="h-4 w-4" />}
                {t("operatorObligationsPanel.changes.createFeedSubmit")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
